'use strict';

import createSlot from "~/helpers/createSlot";
import Loading from "../Loading";
import ErrorBlock from "../ErrorBlock";
import ScrollTarget from "../ScrollTarget";
import StickyContainer from "../StickyContainer";
import Sort from "./Sort";
import Filters from "./Filters";
import Sidebar from "./Sidebar";
import useSearchSort from "~/hooks/useSearchSort";
import { useSearchFilters } from "~/hooks/useSearchFilters";
import Switch from "./filter/Switch";
import LoadingItems from "~/components/search/loading/LoadingItems";

export const DirectionSlot = createSlot()
export const ControlSlot = createSlot()
export const CountSlot = createSlot()
export const ErrorSlot = createSlot()
export const NoItemsSlot = createSlot()
export const ItemsNotFoundSlot = createSlot()
export const BeforeItemsSlot = createSlot()
export const AfterItemsSlot = createSlot()
export const BeforeFilterSlot = createSlot()

export default function SearchLayout({
    layoutRef,

    items,
    isFetching,
    isLoaded,
    isFetchingNextPage,
    isError,
    error,

    renderItem,

    onRefetch,

    emptylegFilterEnabled = false,
    isStickable = true,
    children,
}) {
    const routesRef = React.useRef()
    const contentRef = React.useRef()
    const resultsRef = React.useRef()
    const [stickyProps, setStickyProps] = React.useState({ isSticky: false, wrapHeight: 0, containerHeight: 0, stickyTop: 0 })

    const [selectedSort, setSelectedSort] = React.useState()
    const [selectedFilters, setSelectedFilters] = React.useState({})

    const { sort, sortItems } = useSearchSort()
    const { filterItems, recountFilters, filterFilters, error: filtersError, loading: filtersLoading, loadFilters } = useSearchFilters({ enableIsEmptylegFilter: emptylegFilterEnabled })
    const filters = filterFilters(selectedFilters)
    const emptylegFilterIndex = filters.findIndex(e => 'is_emptyleg' === e.id)
    const emptylegFilter = emptylegFilterIndex >= 0 ? filters.splice(emptylegFilterIndex, 1)[0] : null

    React.useEffect(() => {
        if (items) {
            recountFilters(items)
        }
    }, [items])

    React.useImperativeHandle(layoutRef, () => ({
        clearFilters() { 
            setSelectedFilters({}) 
        },
        scrollTop(speed) {
            routesRef.current.scroll(speed)
        }
    }), [])
    
    const displayItems = React.useMemo(() => {
        if (!items) return []

        const filteredItems = filterItems(items, selectedFilters)
        return sortItems(filteredItems, selectedSort)
    }, [items, selectedFilters, selectedSort])

    if (isError) {
        return <ErrorSlot.Renderer childs={children}>
            <ErrorBlock text={error} onReload={() => onRefetch()} />
        </ErrorSlot.Renderer>
    }

    return <React.Fragment>

        <ScrollTarget offsetDown={-30} offsetUp={-100} ref={routesRef} />

        <StickyContainer isStickable={isStickable} stickyPadding={20} onChange={setStickyProps}>
            <DirectionSlot.Renderer childs={children} />

            <div className="el-sorting">
                <div className="el-sorting__row">
                    {isFetching && <div className="el-sorting__count">
                        <div className="progress-bar">
                            <div className="progress-bar-text">
                                {tMsg('Ищем самолеты...')}
                            </div>
                            <span className="progress-bar-inner" style={{width:"30%"}}></span>
                        </div>
                    </div>}

                    {!isFetching && <div className="el-sorting__count">
                        <React.Fragment>
                            {`${displayItems.length} ${plural(displayItems.length, __('search.results.offers'))}`}
                            <CountSlot.Renderer childs={children}/>
                        </React.Fragment>
                    </div>}

                    <Sort items={sort} value={selectedSort} onChange={setSelectedSort} />

                    <div className="el-sorting__control">
                        <ControlSlot.Renderer childs={children} />
                    </div>
                </div>
            </div>
        </StickyContainer>

        <div className="el-row">

            <Sidebar isLoading={filtersLoading} top={stickyProps.wrapHeight + 20}>

                {!filtersLoading && <React.Fragment>
                    
                    <BeforeFilterSlot.Renderer childs={children} />

                    {emptylegFilter && <div className="el-sidebar__item el-sidebar__single">
                        <Switch title={tMsg('Только Empty Legs')}
                                checked={selectedFilters.is_emptyleg}
                                disabled={emptylegFilter.values.find(e => e.id === '1').count === 0}
                                onChange={checked => setSelectedFilters({ ...selectedFilters, is_emptyleg: checked ? '1' : undefined })}
                        />
                    </div>}

                    <div className="el-sidebar__item">
                        <Filters
                            filters={filters}
                            values={selectedFilters}
                            error={filtersError}
                            onReload={loadFilters}
                            onChange={setSelectedFilters}
                        />
                    </div>
                    
                </React.Fragment>}
                
            </Sidebar>

            <div ref={contentRef} className="el-content">
                <BeforeItemsSlot.Renderer childs={children} />

                <ScrollTarget ref={resultsRef} offset={-90 - (stickyProps.wrapHeight || 0)} className="results">
                    {isFetching && <LoadingItems />}
                    {!isFetching && isLoaded && displayItems.length > 0 && displayItems.map(renderItem)}
                    {!isFetching && isLoaded && items?.length > 0 && displayItems.length === 0 && <NoItemsSlot.Renderer childs={children} />}
                    {!isFetching && isLoaded && items?.length === 0 && <ItemsNotFoundSlot.Renderer childs={children} />}
                    {isFetchingNextPage && <LoadingItems />}
                </ScrollTarget>

                <AfterItemsSlot.Renderer childs={children} />
            </div>
        </div>
    </React.Fragment>
}
